import { useMutation, useReactiveVar } from '@apollo/client'
import { ToggleMergeFieldMutation } from 'api/graphQL/mutations/headerAndFooter.mutation'
import {
    activeHeaderFooter,
    MergeFieldContent,
    setActiveHeaderFooter,
} from 'api/graphQL/reactiveVars/headerFooterTemplate'
import { ToggleMergeFieldPreviewInput } from 'models/projectHeaderAndFooter'
import { useCallback } from 'react'

interface ToggleMergeFieldPreviewResponse {
    toggleMergeFieldPreview: {
        projectId: string
        isEnablePreview: boolean
        isDefault: boolean
        isActive: boolean
        headerMergeFieldContent: MergeFieldContent[]
        footerMergeFieldContent: MergeFieldContent[]
    }
}

export function useToggleMergeFieldPreview() {
    const [toggleMergeFieldPreview, { loading, error }] = useMutation<
        ToggleMergeFieldPreviewResponse,
        { toggleMergeFieldPreviewInput: ToggleMergeFieldPreviewInput }
    >(ToggleMergeFieldMutation, {
        fetchPolicy: 'no-cache',
    })

    const activeHeaderFooterData = useReactiveVar(activeHeaderFooter)

    const handleToggleMergeFieldPreview = useCallback(
        async (
            input: ToggleMergeFieldPreviewInput,
            setHeaderMergeFields: (fields: MergeFieldContent[]) => void,
            setFooterMergeFields: (fields: MergeFieldContent[]) => void
        ) => {
            try {
                const { data } = await toggleMergeFieldPreview({
                    variables: { toggleMergeFieldPreviewInput: input },
                })

                if (data?.toggleMergeFieldPreview) {
                    if (
                        data.toggleMergeFieldPreview.headerMergeFieldContent
                            ?.length
                    ) {
                        setHeaderMergeFields(
                            data.toggleMergeFieldPreview.headerMergeFieldContent
                        )
                    }

                    if (
                        data.toggleMergeFieldPreview.footerMergeFieldContent
                            ?.length
                    ) {
                        setFooterMergeFields(
                            data.toggleMergeFieldPreview.footerMergeFieldContent
                        )
                    }

                    // Update the reactive variable with the new content for header and footer
                    const updatedData = {
                        ...activeHeaderFooterData,
                        projectId: data.toggleMergeFieldPreview.projectId,
                        isEnablePreview:
                            data.toggleMergeFieldPreview.isEnablePreview,
                        isDefault: data.toggleMergeFieldPreview.isDefault,
                        isActive: data.toggleMergeFieldPreview.isActive,
                        headerMergeFieldContent:
                            data.toggleMergeFieldPreview
                                .headerMergeFieldContent,
                        footerMergeFieldContent:
                            data.toggleMergeFieldPreview
                                .footerMergeFieldContent,
                    }

                    // Update the reactive variable, which will trigger a re-render of the component
                    setActiveHeaderFooter(updatedData)
                }
            } catch (error) {
                console.error('Error toggling merge field preview:', error)
            }
        },
        [toggleMergeFieldPreview, activeHeaderFooterData]
    )

    return { handleToggleMergeFieldPreview, loading, error }
}
